import { assertValue } from '@libs/utils';

import type {
  CurrentMonthAlternativeProductDayTile,
  CurrentMonthAvailableDayTile,
  CurrentMonthLoaderDayTile,
  CurrentMonthPastDayTile,
  CurrentMonthUnavailableDayTile,
  PreviousMonthDayTile,
  PriceCalendarTile,
  WeekDayTile,
} from '../models';

// NOTE: This class makes it easy to create calendar view model by encapsulating management of cursor over matrix.
// This means:
// * class instance is **single** use only
// * tiles have to be set **in order**: week days -> previous month -> current month
export class PriceCalendarState {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static daysInAWeek = 7;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static shortMonthWeeks = 6;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static longMonthWeeks = 7;
  private currentWeekIndex = 1;
  private calendarMatrix: PriceCalendarTile[][] = [[], [], [], [], [], [], []];

  public getCalendarMatrix(): PriceCalendarTile[][] {
    return this.calendarMatrix;
  }

  public getTotalTilesInColumn(): number {
    const lastRow = this.calendarMatrix[6];
    assertValue(lastRow, 'priceCalendarLogic.ts > setWeekDayTiles > lastRow');
    return lastRow.length > 0
      ? PriceCalendarState.longMonthWeeks
      : PriceCalendarState.shortMonthWeeks;
  }

  public setWeekDayTiles(tiles: WeekDayTile[]): void {
    assertValue(
      this.calendarMatrix[0],
      'priceCalendarLogic.ts > setWeekDayTiles > this.calendarMatrix[0]'
    );
    this.calendarMatrix[0] = this.calendarMatrix[0].concat(tiles);
  }

  public setPreviousMonthTiles(tiles: PreviousMonthDayTile[]): void {
    assertValue(
      this.calendarMatrix[1],
      'priceCalendarLogic.ts > setCurrentMonthTile > currentWeek 1'
    );
    this.calendarMatrix[1] = this.calendarMatrix[1].concat(tiles);
  }

  public setCurrentMonthTile(
    tile:
      | CurrentMonthPastDayTile
      | CurrentMonthLoaderDayTile
      | CurrentMonthAvailableDayTile
      | CurrentMonthAlternativeProductDayTile
      | CurrentMonthUnavailableDayTile
  ): void {
    this.currentWeek.push(tile);
  }

  private get currentWeek(): PriceCalendarTile[] {
    let currentWeek = this.calendarMatrix[this.currentWeekIndex];

    assertValue(
      currentWeek,
      'priceCalendarLogic.ts > setCurrentMonthTile > currentWeek 1'
    );

    if (currentWeek.length === PriceCalendarState.daysInAWeek) {
      this.currentWeekIndex += 1;
    }
    currentWeek = this.calendarMatrix[this.currentWeekIndex];

    assertValue(
      currentWeek,
      'priceCalendarLogic.ts > setCurrentMonthTile > currentWeek 2'
    );

    return currentWeek;
  }
}
